'use client';

import { createEmailValidator } from '@/components/Form/helper/schemaBuilders';
import { applyServerFieldErrors } from '@/components/Form/helper/serverFieldErrors';
import { Button } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import SubmitWrapper from '@/components/helperTextWrapper/SubmitWrapper';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import { scrollToElement } from '@/helper/scrollToElement';
import { isStreetInCatalog } from '@/helper/streetCatalog';
import tracking from '@/helper/tracking';
import { getFullSlugFromMultilink, linkToString } from '@/helper/urls';
import { generateUUId, resolveStory } from '@/helper/utils';
import { GERMAN_POSTCODE_PATTERN } from '@/helper/validation/proxyInput';
import { useRegionService } from '@/hooks/useRegionService';
import { useB2BStore } from '@/providers/B2BStoreProvider';
import { useHeaderStore } from '@/providers/HeaderStoreProvider';
import { useSettings } from '@/providers/SettingsProvider';
import { yupLocales } from '@/public/locales/de/yupLocales';
import type { PageB2BStoryblok } from '@/types/component-types-sb';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Form, Formik, type FormikHelpers, type FormikProps } from 'formik';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import B2BConsumptionForm from './B2BConsumptionForm';
import { type B2BSubmitData, hasB2BTariffs } from './B2BFormHandler';
import b2bFormHandler from './B2BFormHandler';
import B2BLoadProfiles from './B2BLoadProfiles';
import B2BPersonalDataForm from './B2BPersonalDataForm';
import B2BTariffOverview, { type B2BTariff } from './B2BTariffOverview';
import classes from './PageB2B.module.scss';

export interface B2BFormData {
  shippingPostcode: string;
  shippingCityName: string;
  annualConsumption: string;
  startDate: string;
  greenEnergy: boolean;
  loadProfile: string;
  selectedTariff: B2BTariff;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  postcode: string;
  city: string;
  street: string;
  houseNumber: string;
  uploadedFiles: string;
  comments: string;
}

const PageB2B: React.FC<{
  blok: PageB2BStoryblok;
}> = ({ blok }) => {
  const trackingInformation = useB2BStore((state) => state.trackingInformation);
  const setErrorModalOpen = useHeaderStore((state) => state.setErrorModalOpen);
  const { t } = useTranslation();
  const { inRegion, getRegionsForPostcode } = useRegionService();
  const router = useRouter();
  const settings = useSettings();
  const scrollTo = scrollToElement;
  const refConsumptionForm = useRef<HTMLDivElement>(null);

  const [showTariffs, setShowTariffs] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Set German error messages
  Yup.setLocale(yupLocales);

  const handleShowTariffs = (formikProps: FormikProps<B2BFormData>) => {
    formikProps.validateForm();
    const { annualConsumption, shippingPostcode, shippingCityName, startDate, loadProfile } = formikProps.errors;
    if (!(annualConsumption || shippingPostcode || shippingCityName || startDate || loadProfile)) {
      setShowTariffs(true);
      formikProps.setTouched({});
    } else {
      formikProps.setTouched({
        annualConsumption: true,
        shippingPostcode: true,
        shippingCityName: true,
        startDate: true,
        greenEnergy: true,
        loadProfile: true,
      });
      scrollTo(refConsumptionForm);
    }
  };

  const handleSubmit = async (
    values: B2BFormData,
    { setErrors, setTouched }: Pick<FormikHelpers<B2BFormData>, 'setErrors' | 'setTouched'>
  ) => {
    const loadProfileText = blok.load_profiles.map((item) => {
      if (item.key === values.loadProfile) {
        const loadProfileTimeSpan = item.headline_1;
        const loadProfileTime = item.subtitle_1;
        if (loadProfileTime) {
          return `${loadProfileTimeSpan} | ${loadProfileTime}`;
        }
        return loadProfileTimeSpan;
      }
    });

    const submitData: B2BSubmitData = {
      firstName: values.firstName ?? '',
      lastName: values?.lastName ?? '',
      email: values?.email ?? '',
      phone: values?.phone ?? '',
      company: values?.company ?? '',
      postcode: values?.postcode ?? '',
      city: values?.city ?? '',
      street: values?.street ?? '',
      streetNr: values?.houseNumber ?? '',
      notes: values?.comments ?? '',
      consumption: Number(values?.annualConsumption) ?? 0,
      greenEnergy: values?.greenEnergy ?? false,
      contractStartDate: values?.startDate ?? '',
      uploadSessionId: '',
      duration: values.selectedTariff.duration,
      loadProfile: values?.loadProfile ?? '',
      loadProfileText: loadProfileText?.filter(Boolean)[0] ?? '',
      workingPrice: values.selectedTariff.price.replace('.', ','),
      showPrice: values.selectedTariff.showPrice,
      workingPricePlaceholderText: '',
    };

    const fileFormData = new FormData();
    if (uploadedFiles) {
      uploadedFiles.forEach((file: File) => fileFormData.append('files', file, file.name));
    }

    try {
      const { fieldErrors } = await b2bFormHandler(fileFormData, submitData);

      if (fieldErrors) {
        await applyServerFieldErrors(fieldErrors, { setErrors, setTouched }, t);
        return;
      }

      if (trackingInformation) {
        tracking.nonCommodityPurchase(trackingInformation, {
          transactionId: generateUUId(),
          affiliation: 'b2b',
        });
      }
      const thankYouSlug = getFullSlugFromMultilink(blok.thank_you_page);
      if (thankYouSlug) {
        router.push(linkToString(thankYouSlug));
      }
    } catch (error) {
      console.error('Submit failed', error);
      setErrorModalOpen(true);
    }
  };

  async function isRegion(postCode: string): Promise<boolean> {
    let isInRegion = false;
    if (!blok.regions) {
      return true;
    }

    for (const item of blok.regions) {
      const region = resolveStory(item);
      if (region && (await inRegion(postCode, region.name.toLowerCase()))) {
        isInRegion = true;
      }
    }
    return isInRegion;
  }

  async function hasB2B(postCode: string, city: string): Promise<boolean> {
    // double check
    if (!isRegion(postCode)) {
      return false;
    }

    const isValidB2B = hasB2BTariffs(postCode, city.trim().split(',')[0] ?? '');
    return isValidB2B;
  }

  return (
    <div className={classes.outerPage}>
      <Formik<B2BFormData>
        initialValues={{
          shippingPostcode: '',
          shippingCityName: '',
          annualConsumption: '',
          startDate: '',
          greenEnergy: false,
          loadProfile: blok.load_profiles[0]?.key ?? '',
          selectedTariff: {
            tarifname: '',
            price: '',
            duration: '',
            showPrice: false,
            greenEnergy: false,
          },
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          postcode: '',
          city: '',
          street: '',
          houseNumber: '',
          uploadedFiles: '',
          comments: '',
        }}
        validationSchema={Yup.object({
          shippingPostcode: Yup.string()
            .matches(GERMAN_POSTCODE_PATTERN, t('b2bCalculator.invalidPostcode'))
            .required()
            .test('isInRegion', t('b2bCalculator.notInRegion'), async (value) => {
              if (!value) return false;
              const isValidRegion = await isRegion(value);
              return isValidRegion;
            }),
          shippingCityName: Yup.string()
            .required()
            .test('hasB2B', t('b2bCalculator.notInRegion'), async (value, context) => {
              if (!value || !context.parent.shippingPostcode) return false;
              const isValidB2B = await hasB2B(context.parent.shippingPostcode, value);
              return isValidB2B;
            }),
          annualConsumption: Yup.number()
            .min(Number(settings?.calculator_max_consumption_individual))
            .max(Number(settings?.calculator_max_consumption_b2b))
            .required(),
          startDate: Yup.date()
            .min(dayjs().add(3, 'week').format('YYYY-MM-DD'))
            .max(dayjs().add(1, 'year').format('YYYY-MM-DD'))
            .required(),
          loadProfile: Yup.string().required(),
          firstName: Yup.string().required(),
          lastName: Yup.string().required(),
          email: createEmailValidator().required(),
          street: Yup.string()
            .required()
            .test('street-is-valid', t('Bitte wählen Sie eine Straße aus der Liste'), async function (value) {
              const { postcode, city } = this.parent as { postcode: string; city: string };
              if (!value || !postcode || !city) return false;
              return isStreetInCatalog({ postcode, city }, value);
            }),
          houseNumber: Yup.string().required(),
          postcode: Yup.string().required(),
          city: Yup.string().required(),
          company: Yup.string().required(),
          phone: Yup.string(),
          comments: Yup.string(),
        })}
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const calculatorInvalid =
            !!formikProps.errors.shippingPostcode ||
            !formikProps.values.shippingPostcode ||
            !!formikProps.errors.shippingCityName ||
            !formikProps.values.shippingCityName ||
            !!formikProps.errors.startDate ||
            !formikProps.values.startDate;
          return (
            <Form className={classNames(classes.pageB2B, 'grid-wrapper')}>
              <LoadingBackdrop loading={formikProps.isSubmitting} text={t('Ihre Anfrage wird versendet')} />
              <HeadlineField tag={HeadlineTag.H1} style={HeadlineTag.H3} className={classes.headline}>
                {blok.headline}
              </HeadlineField>
              <B2BConsumptionForm ref={refConsumptionForm} pageB2BData={blok} />
              <B2BLoadProfiles loadProfiles={blok.load_profiles} headline={blok.form_loadprofile_title} />
              {!showTariffs && (
                <SubmitWrapper formikErrors={formikProps.errors} formikTouched={formikProps.touched}>
                  <Button
                    disabled={calculatorInvalid}
                    onClick={() => handleShowTariffs(formikProps)}
                    variant="ctaFull"
                    className={classes.submitButton}
                    data-testid="B2B-page-price-calc-button"
                  >
                    {t('Preis berechnen')}
                  </Button>
                </SubmitWrapper>
              )}
              {showTariffs && !calculatorInvalid && (
                <>
                  <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3} className={classNames(classes.headline)}>
                    {blok.tariffs_title}
                  </HeadlineField>
                  <B2BTariffOverview pageB2BData={blok} />
                  {!!formikProps.values.selectedTariff.duration && (
                    <>
                      <B2BPersonalDataForm onFilesChanged={setUploadedFiles} />
                      <SubmitWrapper formikErrors={formikProps.errors} formikTouched={formikProps.touched}>
                        <Button
                          data-testid="B2B-page-request-send-button"
                          type="submit"
                          variant="ctaFull"
                          fullWidth
                          className={classes.submitButton}
                        >
                          {t('Anfrage senden')}
                        </Button>
                      </SubmitWrapper>
                    </>
                  )}
                </>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PageB2B;
